import { createContext, useContext, useEffect, useState, useMemo, PropsWithChildren } from 'react';
import { LDClient as LDClientType, LDFlagSet, initialize } from 'launchdarkly-js-client-sdk';

import { PageLoader } from '@/components/shared/loaders/PageLoader';
import { SignOutHandler } from '@/components/shared/auth/SignOutHandler';
import { IdleSessionTimeout } from '@/components/App/IdleSessionTimeout';
import { useSetFeatureFlagProviderContext } from '@/hooks/featureFlag/useGenerateSecureFeatureFlagContext';
import { Config } from '@/utils/environment';

interface LaunchDarklyContextValue {
  LDClient: LDClientType | null;
  flags: LDFlagSet;
  isClientInitialized: boolean;
  invalidateClient: () => void;
  closeLDClient: () => void;
}

const LaunchDarklyContext = createContext<LaunchDarklyContextValue>({
  LDClient: null,
  flags: {},
  isClientInitialized: false,

  invalidateClient: () => { },

  closeLDClient: () => { },
});

export const useLaunchDarkly = () => {
  const context = useContext(LaunchDarklyContext);
  if (!context) {
    throw new Error('useLaunchDarkly must be used within LaunchDarklyProvider');
  }
  return context;
};

type LaunchDarklyGatewayProps = PropsWithChildren;

export const LaunchDarklyGateway = ({ children }: LaunchDarklyGatewayProps) => {
  const [LDClient, setLDClient] = useState<LDClientType | null>(null);
  const [flags, setFlags] = useState<LDFlagSet>({});
  const [isInitializationAttempted, setIsInitializationAttempted] = useState(false);
  const [isClientInitialized, setIsClientInitialized] = useState(false);

  const {
    isLoading: isContextLoading,
    multiContext,
    hash,
  } = useSetFeatureFlagProviderContext();

  // Initialize client only once when context is first available
  useEffect(() => {
    if (isContextLoading || !multiContext || !hash || LDClient) {
      return;
    }

    const config = Config.getAll();
    const clientId = config.VITE_LAUNCHDARKLY_CLIENT_ID;
    if (!clientId) {
      console.error('LaunchDarkly client ID not found in environment variables');
      setIsInitializationAttempted(true);
      return;
    }

    let launchDarklyClient: LDClientType;

    const initializeClient = async () => {
      try {
        const options = {
          hash,
        };

        launchDarklyClient = initialize(clientId, multiContext, options);

        await launchDarklyClient.waitForInitialization(3);

        setIsClientInitialized(true);
        setLDClient(launchDarklyClient);
        setFlags(launchDarklyClient.allFlags());

        const handleFlagsChange = () => {
          setFlags(launchDarklyClient.allFlags());
        };

        launchDarklyClient.on('change', handleFlagsChange);

        return () => {
          launchDarklyClient.off('change', handleFlagsChange);
        };
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to initialize LaunchDarkly');
        console.error('Failed to initialize LaunchDarkly:', error);
      } finally {
        setIsInitializationAttempted(true);
      }
    };

    initializeClient();
  }, [isContextLoading, multiContext, hash, LDClient]);

  // Handle context changes using identify() method instead of recreating client
  useEffect(() => {
    if (!LDClient || !isClientInitialized || isContextLoading || !multiContext || !hash) {
      return;
    }

    const updateContext = async () => {
      try {
        await LDClient.identify(multiContext, hash);
        setFlags(LDClient.allFlags());
      } catch (error) {
        console.error('Failed to update LaunchDarkly context:', error);
      }
    };

    updateContext();
  }, [LDClient, isClientInitialized, multiContext, hash, isContextLoading]);

  const invalidateClient = useMemo(() => async () => {
    if (!LDClient) {
      return;
    }

    await LDClient.flush();
    await LDClient.close();
    setLDClient(null);
    setFlags({});
    setIsClientInitialized(false);
    setIsInitializationAttempted(false);
  }, [LDClient]);

  const closeLDClient = useMemo(() => async () => {
    if (LDClient) {
      try {
        await LDClient.close();
        setLDClient(null);
        setFlags({});
        setIsClientInitialized(false);
      } catch (error) {
        console.error('Failed to close LaunchDarkly client:', error);
      }
    }
  }, [LDClient]);

  const contextValue: LaunchDarklyContextValue = useMemo(() => ({
    LDClient,
    flags,
    isClientInitialized,
    invalidateClient,
    closeLDClient,
  }), [LDClient, flags, isClientInitialized, invalidateClient, closeLDClient]);

  if (isContextLoading || !isInitializationAttempted) {
    return <PageLoader />;
  }

  return (
    <LaunchDarklyContext.Provider value={contextValue}>
      <SignOutHandler>
        {(signOut, isSigningOut) => (
          <IdleSessionTimeout signOut={signOut} isSigningOut={isSigningOut}>
            {children}
          </IdleSessionTimeout>
        )}
      </SignOutHandler>
    </LaunchDarklyContext.Provider>
  );
};
